<template>
  <div class="workflow-list">
    <div class="header">
      <div class="title-wrap">
        <h3>{{ sectionTitle }}</h3>
        <span class="meta-count">{{ (elements || []).length }} item(s)</span>
      </div>
      <button @click="openCreateModal" class="new-workflow-button hide-on-collapse">
        New {{ typeDisplayName }}
      </button>
    </div>

    <div class="search-filter-row">
      <input type="text" v-model="searchQuery" placeholder="Search by Name..." class="search-input">
      <button @click="showFilters = !showFilters" class="toggle-filter-button">
        {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
      </button>
    </div>

    <div v-if="showFilters" class="filters">
      <input type="text" v-model="typeFilter" class="date-input" placeholder="Type">
    </div>

    <table class="workflow-table">
      <thead>
        <tr>
          <th v-if="hasPositionOrdering" class="actions-col">Actions</th>
          <th v-if="hasPositionOrdering" @click="sortElements('position')">Order<i v-if="currentSortColumn === 'position'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          <th @click="sortElements('name')">Name<i v-if="currentSortColumn === 'name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          <th @click="sortElements('type_name')">Type<i v-if="currentSortColumn === 'type_name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="element in paginatedElements" :key="element.id" class="workflow-row" @click="openElementDetail(element)">
          <td v-if="hasPositionOrdering" class="row-actions" @click.stop>
            <button
              type="button"
              class="icon-btn action-arrow"
              :disabled="isReorderingElementId === element.id || !canMoveElement(element, 'up')"
              title="Move up"
              @click.stop="moveElement(element, 'up')"
            >
              <i class="bi bi-chevron-up"></i>
            </button>
            <button
              type="button"
              class="icon-btn action-arrow"
              :disabled="isReorderingElementId === element.id || !canMoveElement(element, 'down')"
              title="Move down"
              @click.stop="moveElement(element, 'down')"
            >
              <i class="bi bi-chevron-down"></i>
            </button>
          </td>
          <td v-if="hasPositionOrdering">{{ formatPosition(element.position) }}</td>
          <td>{{ element.name || '-' }}</td>
          <td>{{ element.type_name || '-' }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button v-for="page in visiblePageNumbers" :key="page" @click="setPage(page)" :class="{ active: page === currentPage }">{{ page }}</button>
    </div>

    <div
      v-if="showCreateModal"
      class="create-workflow-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="`Create ${typeDisplayName}`"
      @click.self="closeCreateModal"
    >
      <div class="create-workflow-modal">
        <div class="create-workflow-header">
          <div>
            <h4>Create {{ typeDisplayName }}</h4>
            <p>{{ projectName }}</p>
          </div>
          <button
            type="button"
            class="workflow-modal-close-btn"
            :aria-label="`Close ${typeDisplayName} creation`"
            @click="closeCreateModal"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <form class="create-workflow-form" @submit.prevent="submitCreateElement">
          <div class="form-group">
            <label for="child-element-name">Name</label>
            <input
              id="child-element-name"
              v-model.trim="createForm.name"
              type="text"
              class="form-control"
              required
            />
          </div>

          <div v-if="isLocationRequired" class="form-group">
            <label for="child-element-location">Location</label>
            <select
              id="child-element-location"
              v-model="createForm.location_id"
              class="form-control"
              required
            >
              <option disabled value="">Select a Location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">
                {{ location.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="child-element-status">Status</label>
            <select id="child-element-status" v-model="createForm.status" class="form-control">
              <option value="To be Started">To be Started</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Finished">Finished</option>
            </select>
          </div>

          <div class="create-workflow-actions">
            <button type="button" class="btn-secondary" @click="closeCreateModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting">Creating...</span>
              <span v-else>Create {{ typeDisplayName }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

const defaultCreateForm = () => ({
  name: '',
  parent_element_id: null,
  location_id: '',
  status: 'To be Started',
});

export default {
  name: 'ProjectChildElementsList',
  props: {
    elements: {
      type: Array,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
    structureSetId: {
      type: Number,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
    sectionTitle: {
      type: String,
      default: 'Child Elements',
    },
    parentElementId: {
      type: Number,
      default: null,
    },
    isOrderedList: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      elementsPerPage: 5,
      currentSortColumn: 'position',
      currentSortDirection: 'asc',
      showFilters: false,
      typeFilter: '',
      sortedElements: [],
      showCreateModal: false,
      isSubmitting: false,
      createForm: defaultCreateForm(),
      selectedTypeId: null,
      availableChildTypes: [],
      locations: [],
      isReorderingElementId: null,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),

    hasPositionOrdering() {
      return this.isOrderedList;
    },

    selectedType() {
      const selectedId = Number(this.selectedTypeId);
      return this.availableChildTypes.find((type) => Number(type.id) === selectedId) || null;
    },

    isLocationRequired() {
      return !!this.selectedType?.requires_location;
    },

    typeDisplayName() {
      const fromAllowed = this.availableChildTypes?.[0]?.name;
      if (fromAllowed) {
        return fromAllowed;
      }

      const normalized = String(this.sectionTitle || '').trim();
      if (!normalized) {
        return 'Element';
      }

      const lower = normalized.toLowerCase();
      if (lower.endsWith('ies')) {
        return `${normalized.slice(0, -3)}y`;
      }
      if (lower.endsWith('s') && normalized.length > 1) {
        return normalized.slice(0, -1);
      }
      return normalized;
    },

    filteredElements() {
      return this.sortedElements.filter((element) => {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch = (element.name || '').toLowerCase().includes(query);
        const matchesType = this.typeFilter === '' || (element.type_name || '').toLowerCase().includes(this.typeFilter.toLowerCase());
        return matchesSearch && matchesType;
      });
    },

    paginatedElements() {
      const start = (this.currentPage - 1) * this.elementsPerPage;
      const end = start + this.elementsPerPage;
      return this.filteredElements.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredElements.length / this.elementsPerPage);
    },

    visiblePageNumbers() {
      const range = 10;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }
      return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.applyDefaultSort(this.elements);
  },
  watch: {
    elements(newVal) {
      this.applyDefaultSort(newVal);
    },
    isOrderedList() {
      this.applyDefaultSort(this.elements || []);
    },
  },
  methods: {
    applyDefaultSort(items = []) {
      const list = [...items];

      if (this.hasPositionOrdering) {
        this.currentSortColumn = 'position';
        this.currentSortDirection = 'asc';
        list.sort((a, b) => {
          const left = Number.isFinite(Number(a?.position)) ? Number(a.position) : Number.MAX_SAFE_INTEGER;
          const right = Number.isFinite(Number(b?.position)) ? Number(b.position) : Number.MAX_SAFE_INTEGER;
          return left - right;
        });
      } else {
        this.currentSortColumn = 'name';
        this.currentSortDirection = 'asc';
        list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      }

      this.sortedElements = list;
    },

    async openCreateModal() {
      this.createForm = defaultCreateForm();
      this.selectedTypeId = null;
      this.availableChildTypes = [];
      this.locations = [];
      this.showCreateModal = true;
      await this.fetchAllowedChildTypes();
      if (this.isLocationRequired) {
        await this.fetchLocations();
      }
    },

    closeCreateModal() {
      if (this.isSubmitting) return;
      this.showCreateModal = false;
    },

    async fetchAllowedChildTypes() {
      if (!this.showCreateModal || !this.loggedInUser?.entra_id) {
        return;
      }
      try {
        const response = await apiService.getProjectStructureAllowedChildTypes(
          this.projectId,
          this.parentElementId,
          this.loggedInUser.entra_id,
          this.structureSetId
        );
        this.availableChildTypes = this.normalizeCollection(response, 'types');
        this.selectedTypeId = this.availableChildTypes.length ? this.availableChildTypes[0].id : null;
      } catch (error) {
        this.selectedTypeId = null;
        this.availableChildTypes = [];
        this.toast.error('Failed to fetch allowed child types.');
      }
    },

    async fetchLocations() {
      if (!this.loggedInUser?.entra_id) {
        return;
      }

      try {
        const response = await apiService.get(API_ENDPOINTS.locations, {}, this.loggedInUser.entra_id);
        this.locations = this.normalizeCollection(response, 'locations');
      } catch (error) {
        this.locations = [];
        this.toast.error('Failed to fetch locations.');
      }
    },

    normalizeCollection(payload, key) {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.items)) return payload.items;
      if (payload && Array.isArray(payload[key])) return payload[key];
      if (payload && payload.data && Array.isArray(payload.data.items)) return payload.data.items;
      if (payload && payload.data && Array.isArray(payload.data)) return payload.data;
      return [];
    },

    async submitCreateElement() {
      if (!this.createForm.name || !this.selectedTypeId) {
        this.toast.error('Name and type are required.');
        return;
      }

      if (this.isLocationRequired && !this.createForm.location_id) {
        this.toast.error('Location is required for this type.');
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = {
          name: this.createForm.name,
          type_id: Number(this.selectedTypeId),
          parent_element_id: this.parentElementId,
          status: this.createForm.status,
        };

        if (this.isLocationRequired) {
          payload.location_id = Number(this.createForm.location_id);
        }

        await apiService.createProjectStructureElement(
          this.projectId,
          payload,
          this.loggedInUser.entra_id,
          this.structureSetId
        );

        this.toast.success('Child element created.');
        this.showCreateModal = false;
        this.$emit('refresh-elements');
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to create child element.');
      } finally {
        this.isSubmitting = false;
      }
    },

    sortElements(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSortDirection = 'asc';
        this.currentSortColumn = column;
      }

      this.sortedElements.sort((a, b) => {
        const isNumericSort = column === 'position';
        const left = isNumericSort
          ? (Number.isFinite(Number(a?.[column])) ? Number(a[column]) : Number.MAX_SAFE_INTEGER)
          : (a?.[column] ?? '');
        const right = isNumericSort
          ? (Number.isFinite(Number(b?.[column])) ? Number(b[column]) : Number.MAX_SAFE_INTEGER)
          : (b?.[column] ?? '');

        if (left < right) return this.currentSortDirection === 'asc' ? -1 : 1;
        if (left > right) return this.currentSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },

    formatPosition(position) {
      const numeric = Number(position);
      if (!Number.isFinite(numeric)) {
        return '-';
      }
      return numeric + 1;
    },

    getOrderedElements() {
      return [...this.sortedElements]
        .filter((item) => Number.isFinite(Number(item?.position)))
        .sort((a, b) => Number(a.position) - Number(b.position));
    },

    canMoveElement(element, direction) {
      if (!this.hasPositionOrdering) {
        return false;
      }

      const ordered = this.getOrderedElements();
      const index = ordered.findIndex((item) => Number(item.id) === Number(element.id));
      if (index < 0) {
        return false;
      }

      if (direction === 'up') {
        return index > 0;
      }

      if (direction === 'down') {
        return index < ordered.length - 1;
      }

      return false;
    },

    async moveElement(element, direction) {
      if (!this.hasPositionOrdering || this.isReorderingElementId) {
        return;
      }

      const ordered = this.getOrderedElements();
      const sourceIndex = ordered.findIndex((item) => Number(item.id) === Number(element.id));
      if (sourceIndex < 0) {
        return;
      }

      const targetIndex = direction === 'up' ? sourceIndex - 1 : sourceIndex + 1;
      if (targetIndex < 0 || targetIndex >= ordered.length) {
        return;
      }

      const previous = [...this.sortedElements];
      const reordered = [...ordered];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      const positionById = new Map(reordered.map((item, idx) => [Number(item.id), idx]));
      this.sortedElements = this.sortedElements.map((item) => {
        const key = Number(item.id);
        if (!positionById.has(key)) {
          return item;
        }
        return {
          ...item,
          position: positionById.get(key),
        };
      });

      this.isReorderingElementId = Number(element.id);

      try {
        const normalizedParentElementId = this.parentElementId == null
          ? null
          : Number(this.parentElementId);

        await apiService.moveProjectStructureElementChild(
          normalizedParentElementId,
          Number(element.id),
          targetIndex,
          this.loggedInUser.entra_id,
          this.structureSetId
        );
        this.toast.success('Element order updated.');
        this.$emit('refresh-elements');
      } catch (error) {
        this.sortedElements = previous;
        this.toast.error(error?.response?.data?.message || 'Failed to reorder element.');
      } finally {
        this.isReorderingElementId = null;
      }
    },

    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },

    setArrowDirection(sortDirection) {
      return sortDirection === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
    },

    openElementDetail(element) {
      if (!element?.id) {
        return;
      }

      this.$router.push({
        name: 'StructureElementDetailPage',
        params: {
          projectId: this.projectId,
          elementId: element.id,
        },
        query: this.structureSetId != null ? { structure_set_id: this.structureSetId } : undefined,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.workflow-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 26px;
    padding-right: 58px;

    h3 {
      margin: 0;
      color: $primary-color;
    }

    .title-wrap {
      display: flex;
      align-items: baseline;
      gap: 14px;
    }

    .meta-count {
      font-size: 0.92rem;
      font-weight: 500;
      color: rgba($primary-color, 0.8);
    }

    .new-workflow-button {
      padding: 8px 16px;
      background-color: $primary-color;
      color: #fff;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .new-workflow-button:hover {
      background-color: darken($primary-color, 10%);
    }
  }

  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .search-input {
    flex: 1;
    margin: 0;
  }

  .toggle-filter-button {
    width: auto;
    margin: 0;
    padding: 6px 10px;
    white-space: nowrap;
  }

  .workflow-table {
    width: 100%;
  }

  .actions-col {
    width: 92px;
    text-align: center;
  }

  .workflow-table th {
    background-color: $form-color;
    border: 1px solid $primary-color;
    cursor: pointer;
  }

  .filters {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: none;
    margin: 6px 0;
    gap: 8px;
  }

  .workflow-row {
    background-color: $secondary-color;
  }

  .workflow-row:hover {
    background-color: $form-color;
    cursor: pointer;
  }

  .row-actions {
    display: flex;
    justify-content: center;
    gap: 6px;
  }

  .action-arrow {
    border: 0;
    background: transparent;
    color: $primary-color;
    padding: 2px 4px;
  }

  .action-arrow:disabled {
    color: #a5a5a5;
    cursor: not-allowed;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }
}

.create-workflow-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.create-workflow-modal {
  width: min(560px, 100%);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  padding: 24px;
}

.create-workflow-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  h4 {
    margin: 0;
    color: $primary-color;
  }

  p {
    margin: 4px 0 0;
    color: #5a5a5a;
  }
}

.workflow-modal-close-btn {
  border: 1px solid rgba($primary-color, 0.18);
  background: rgba(255, 255, 255, 0.92);
  color: $primary-color;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.create-workflow-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    color: $primary-color;
  }
}

.form-control {
  width: 100%;
  min-height: 42px;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: #222;
}

.create-workflow-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.btn-secondary {
  border: 1px solid rgba($primary-color, 0.2);
  background: #fff;
  color: $primary-color;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary {
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
